

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getArticleBySlug, getLatestArticles } from "../services/api";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import "../styles/articles.css";
import SeoHead from '../components/SeoHead.jsx';

const Base_URL = process.env.REACT_APP_Base_URL;
const translations = {
  en: { minRead: "Min read" },
  ja: { minRead: "分で読めます" },
};

export default function ArticleDetail({ lang }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const getImageUrl = (img) => {
    const image = Array.isArray(img) ? img[0] : img;
    const url =
      image?.formats?.large?.url ||
      image?.formats?.medium?.url ||
      image?.formats?.small?.url ||
      image?.formats?.thumbnail?.url ||
      image?.url;
    if (!url) return "/fallback.jpg";
    const cleanUrl = url.replace(/^https?:\/\/[^/]+/, "");
    return `${Base_URL}${cleanUrl}`;
  };

  useEffect(() => {
    setLoading(true);
    setArticle(null);
    setError(null);

    const loadArticle = async () => {
      try {
        let data = await getArticleBySlug(slug, lang);
        if (!data && lang !== "en") {
          data = await getArticleBySlug(slug, "en");
        }
        if (data) {
          setArticle(data);
        } else {
          setError("Article not found");
        }
      } catch {
        try {
          const fallback = await getArticleBySlug(slug, "en");
          if (fallback) setArticle(fallback);
          else setError("Article not found");
        } catch {
          setError("Article not found");
        }
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [slug, lang]);

  useEffect(() => {
    const loadRelated = async () => {
      try {
        // let articles = await getLatestArticles(lang, 5);
        let articles = await getLatestArticles(1, 5, lang);
        // if (!articles?.length && lang !== "en") {
        //   articles = await getLatestArticles("en", 5);
        // }
        if (!articles?.length && lang !== "en") {
  articles = await getLatestArticles(1, 5, "en");
}
        const filtered = (articles || [])
          .filter((a) => a.slug !== slug)
          .slice(0, 4);
        setRelatedArticles(filtered);
      } catch {
        setRelatedArticles([]);
      }
    };
    loadRelated();
  }, [slug, lang]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${day} ${months[Number(month) - 1]}, ${year}`;
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > window.innerHeight - 150);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (loading) return <p className="p-8 text-center">Loading article...</p>;
  if (error) return <p className="p-8 text-center text-red-500">{error}</p>;
  if (!article) return null;

  return (
    <div>
      
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className={`back-btn ${scrolled ? "back-btn-scrolled" : ""}`}
      >
           {lang === "ja" ? "戻る" : "Go Back"}
        {" "}
        <svg className="ml-2.5" xmlns="http://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 15 12" fill="none">
          <path d="M13.75 5.75L0.75 5.75M0.75 5.75L5.625 10.75M0.75 5.75L5.625 0.75" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Cover Image */}
      <div className="relative">
        {article.cover_image && (
          <img
            style={{ height: "100dvh", width: "100dvw", objectFit: "cover" }}
            src={getImageUrl(article?.cover_image)}
            alt={article?.title}
          />
        )}
        <div style={{ position: "absolute", bottom: "20px" }} className="padding-x-40 pt-[80px]">
          <div className="section-name-wrapper">
            <div className="section-name-dot" style={{ backgroundColor: "#fff" }}></div>
            <h6 className="section-name" style={{ color: "#fff" }}>
              {lang === "ja" ? "私たちのストーリー" : "Our Stories"}
            </h6>
          </div>
          <h1 className="article-full-heading mb-4 text-white">{article.Title}</h1>
        </div>
      </div>

      {/* Date, Tags & Read Time */}
      <div className="flex flex-wrap padding-x-40 padding-y-80 md:w-[75%] mx-auto justify-between" >
        <div className="w-[32%] flex flex-col" style={{ borderTop: "1px solid rgba(25, 175, 225, 1)", paddingTop: "15px" }}>
          <span className="founder-resource-details text-dark-blue-color" style={{ paddingBottom: ".5rem" }}>
            {lang === "ja" ? "日付" : "Date"}
          </span>
          <small className="text-dark-blue-color font-medium">{formatDate(article.published_date)}</small>
        </div>
        <div className="w-[32%] flex flex-col" style={{ borderTop: "1px solid rgba(25, 175, 225, 1)", paddingTop: "15px" }}>
          <span className="founder-resource-details text-dark-blue-color" style={{ paddingBottom: ".5rem" }}>
            {lang === "ja" ? "カテゴリ" : "Category"}
          </span>
          <span className="text-dark-blue-color font-medium">
            {Array.isArray(article.tags) && article.tags.length > 0
              ? article.tags.map((t) => t.Name || t.name).join(", ")
              : "—"}
          </span>
        </div>
        <div className="w-[32%] flex flex-col" style={{ borderTop: "1px solid rgba(25, 175, 225, 1)", paddingTop: "15px" }}>
          <span className="founder-resource-details text-dark-blue-color" style={{ paddingBottom: ".5rem" }}>    {lang === "ja" ? "読書時間" : "Reading Time"}</span>
          <small className="text-dark-blue-color font-medium">
            {article.read_time || 1} {translations[lang]?.minRead || translations.en.minRead}
          </small>
        </div>
      </div>

      {/* Article Description */}
      <div className="text-dark-blue-color md:w-[75%] mx-auto country-map-text padding-x-40 custom-width-75">
        {article.article_description && Array.isArray(article.article_description) && (
          <BlocksRenderer content={article.article_description} />
        )}
      </div>

      {/* Full Content */}
      {article.content && Array.isArray(article.content) && (() => {
        const imageCount = article.content.filter((block) => block.type === "image").length;
        let imgNum = 0;
        const imageClassMap = article.content.reduce((map, block, i) => {
          if (block.type === "image") {
            imgNum += 1;
            map[i] = imgNum === 1 || imgNum === imageCount ? "article-img-full" : "article-img-inline";
          }
          return map;
        }, {});

        return (
          <div className="mb-6 text-dark-blue-color padding-x-40 padding-y-80 article-rich-text" style={{paddingBottom:'40px'}}>
            <BlocksRenderer
              content={article.content}
              blocks={{
                image: ({ image }) => {
                  const blockIndex = article.content.findIndex(
                    (b) => b.type === "image" && b.image?.url === image.url
                  );
                  const className = imageClassMap[blockIndex] || "article-img-inline";
                  const cleanUrl = image.url.replace(/^https?:\/\/[^/]+/, "");
                  return (
                    <img
                      src={`${Base_URL}${cleanUrl}`}
                      alt={image.alternativeText || ""}
                      className={className}
                    />
                  );
                },
              }}
            />
          </div>
        );
      })()}

      {/* Related Articles */}
      <div className="padding-y-80" style={{ backgroundColor: "rgba(188, 226, 245, 1)" }}>
        {relatedArticles.length > 0 && (
          <div className="related-articles padding-x-40 ">
            <div className="section-name-wrapper mb-6 pb-6">
              <div className="section-name-dot"></div>
              {/* <h6 className="section-name">Similar to This</h6> */}
              <h6 className="section-name">{lang === "ja" ? "関連記事" : "Similar to This"}</h6>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedArticles.map((a) => (
                <div
                  key={a.slug}
                  className="related-article-card cursor-pointer"
                  // onClick={() => navigate(`/articles/${a.slug}`)}
                  onClick={() => {
  const jaSlug = a.slug.startsWith("ja-") ? a.slug : `ja-${a.slug}`;
  const enSlug = a.slug.replace(/^ja-/, "");
  navigate(lang === "ja" ? `/ja/articles/${jaSlug}` : `/articles/${enSlug}`);
}}
                  // onClick={() => navigate(lang === "ja" ? `/ja/articles/${a.slug}` : `/articles/${a.slug}`)}
                >
                  {a.cover_image && (
                    <img
                      src={`${Base_URL}${a.cover_image}`}
                      alt={a.title}
                      className="w-full  object-cover rounded-md mb-3 article-thumbnails"
                    />
                  )}
                  <div className="flex items-baseline">
                    {/* <small className="article-date text-accent-blue block mb-1 mr-1">
                      {formatDate(a.published_date)}
                    </small> */}
                    <small className="article-date text-accent-blue block mb-1 mr-1">
  {a.published_date.replace(/-/g, ".")}
</small>
                    <div className="section-name-dot" style={{ marginRight: "0" }}></div>
                    <small className="text-accent-blue article-date ml-1">
                      {a.read_time || 1} {translations[lang]?.minRead || translations.en.minRead}
                    </small>
                  </div>
                  <h3 className="article-list-title truncate">{a.title}</h3>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}